import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TimerIcon from '@mui/icons-material/Timer';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';

import {
  DurationLargestUnit,
  LifeEventsDisplaySettings,
  useLifeEventsDisplaySettings,
} from '../contexts/PreferencesContext';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

export type TimerRecurrence = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
export type TimerRecurrenceUnit = 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years';
export type TimerState =
  | 'active'
  | 'paused'
  | 'completed'
  | 'expired'
  | 'not_started'
  | 'archived';

export interface Timer {
  id: number;
  user_id: number;
  title: string;
  description?: string | null;
  target_date: string;
  timer_type: 'count_up' | 'countdown';
  is_recurring: boolean;
  recurrence_type: TimerRecurrence;
  recurrence_interval: number;
  recurrence_unit?: TimerRecurrenceUnit | null;
  recurrence_anchor_date?: string | null;
  tags: string[];
  color: string;
  is_active: boolean;
  last_reset_at?: string | null;
  // Set when soft-archived; null/absent for live timers.
  archived_at?: string | null;
  next_occurrence_at?: string | null;
  state?: TimerState;
  duration_seconds?: number;
  created_at: string;
  updated_at?: string | null;
}

interface TimerCardProps {
  timer: Timer;
  onEdit?: (timer: Timer) => void;
  onDelete?: (timer: Timer) => void;
  onStart?: (timer: Timer) => void;
  onPause?: (timer: Timer) => void;
  onReset?: (timer: Timer) => void;
  onArchive?: (timer: Timer) => void;
  onRestore?: (timer: Timer) => void;
  compact?: boolean;
  /**
   * Shared clock tick (epoch ms or Date). When provided the card renders from
   * it and creates no interval of its own — pages rendering many cards should
   * pass one shared tick. When absent the card keeps its own 1s interval so
   * standalone mounts (e.g. Dashboard) still update.
   */
  now?: Date | number;
}

export interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
  isPast: boolean;
}

const addMonthsClamped = (date: Date, months: number): Date => {
  const result = new Date(date.getTime());
  const originalDay = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(originalDay, lastDay));
  return result;
};

const addInterval = (date: Date, interval: number, unit: TimerRecurrenceUnit): Date => {
  const result = new Date(date.getTime());
  switch (unit) {
    case 'minutes':
      result.setMinutes(result.getMinutes() + interval);
      return result;
    case 'hours':
      result.setHours(result.getHours() + interval);
      return result;
    case 'days':
      result.setDate(result.getDate() + interval);
      return result;
    case 'weeks':
      result.setDate(result.getDate() + interval * 7);
      return result;
    case 'months':
      return addMonthsClamped(result, interval);
    case 'years':
      return addMonthsClamped(result, interval * 12);
    default:
      return result;
  }
};

const recurrenceUnitForTimer = (timer: Timer): TimerRecurrenceUnit | null => {
  switch (timer.recurrence_type) {
    case 'daily':
      return 'days';
    case 'weekly':
      return 'weeks';
    case 'monthly':
      return 'months';
    case 'yearly':
      return 'years';
    case 'custom':
      return timer.recurrence_unit || null;
    default:
      return null;
  }
};

export const calculateNextOccurrence = (timer: Timer, now = new Date()): Date | null => {
  if (!timer.is_recurring || timer.recurrence_type === 'none') {
    return null;
  }
  if (timer.next_occurrence_at) {
    const serverNext = new Date(timer.next_occurrence_at);
    if (serverNext > now) return serverNext;
  }

  const unit = recurrenceUnitForTimer(timer);
  if (!unit) return null;
  const interval = Math.max(1, timer.recurrence_interval || 1);
  let candidate = new Date(timer.recurrence_anchor_date || timer.target_date);
  if (candidate > now) return candidate;

  if (unit === 'minutes' || unit === 'hours' || unit === 'days' || unit === 'weeks') {
    const secondsByUnit: Record<string, number> = {
      minutes: 60,
      hours: 3600,
      days: 86400,
      weeks: 604800,
    };
    const intervalMs = secondsByUnit[unit] * interval * 1000;
    const elapsed = Math.max(0, now.getTime() - candidate.getTime());
    const steps = Math.floor(elapsed / intervalMs) + 1;
    candidate = new Date(candidate.getTime() + steps * intervalMs);
  }

  while (candidate <= now) {
    candidate = addInterval(candidate, interval, unit);
  }
  return candidate;
};

export const getTimerDisplayTarget = (timer: Timer, now = new Date()): Date => {
  if (timer.timer_type === 'countdown' && timer.is_recurring) {
    return calculateNextOccurrence(timer, now) || new Date(timer.target_date);
  }
  return new Date(timer.target_date);
};

export const calculateTimeRemaining = (timer: Timer, now = new Date()): TimeRemaining => {
  const target = getTimerDisplayTarget(timer, now);
  const diff = timer.timer_type === 'countdown'
    ? target.getTime() - now.getTime()
    : now.getTime() - target.getTime();

  const isPast = timer.timer_type === 'countdown' && diff < 0;
  const absDiff = Math.abs(diff);

  const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((absDiff % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, total: absDiff, isPast };
};

/** The duration-display slice of life_events_display that the card honours. */
export type DurationDisplayConfig = Pick<
  LifeEventsDisplaySettings,
  | 'duration_largest_unit'
  | 'duration_precision'
  | 'duration_show_clock'
  | 'duration_show_workdays'
>;

export interface DurationRollup {
  /** Calendar units, largest first, truncated to duration_precision. */
  units: Array<{ unit: DurationLargestUnit; value: number }>;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}

const ROLLUP_CHAINS: Record<DurationLargestUnit, DurationLargestUnit[]> = {
  years: ['years', 'months', 'weeks', 'days'],
  months: ['months', 'weeks', 'days'],
  weeks: ['weeks', 'days'],
  days: ['days'],
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Roll the timer's remaining/elapsed span up into calendar units. Years and
 * months use real calendar arithmetic (with end-of-month clamping) and the
 * remainder is measured from the advanced cursor — never a 30-day
 * approximation. With the default config (largest unit days, precision 1,
 * clock on) the values are exactly those of calculateTimeRemaining.
 */
export const computeDurationRollup = (
  timer: Timer,
  config: DurationDisplayConfig,
  now = new Date(),
): DurationRollup => {
  const target = getTimerDisplayTarget(timer, now);
  const diff = timer.timer_type === 'countdown'
    ? target.getTime() - now.getTime()
    : now.getTime() - target.getTime();
  const isPast = timer.timer_type === 'countdown' && diff < 0;

  const start = new Date(Math.min(now.getTime(), target.getTime()));
  const end = new Date(Math.max(now.getTime(), target.getTime()));

  const precision = Math.min(4, Math.max(1, Math.floor(config.duration_precision) || 1));
  const chain = ROLLUP_CHAINS[config.duration_largest_unit].slice(0, precision);

  const units: DurationRollup['units'] = [];
  let cursor = start;

  if (chain.includes('years') || chain.includes('months')) {
    // Largest whole-month count that fits in the span. Start from an
    // over-estimate and walk down; the clamped adder is monotonic in the
    // month count, so the first fit is the answer.
    let wholeMonths =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) +
      1;
    while (wholeMonths > 0 && addMonthsClamped(start, wholeMonths) > end) {
      wholeMonths -= 1;
    }
    let consumedMonths = wholeMonths;
    if (chain.includes('years')) {
      const years = Math.floor(wholeMonths / 12);
      units.push({ unit: 'years', value: years });
      if (chain.includes('months')) {
        units.push({ unit: 'months', value: wholeMonths - years * 12 });
      } else {
        // Precision cut the chain at years: only whole years are consumed,
        // and the leftover months feed nothing but the sub-day clock.
        consumedMonths = years * 12;
      }
    } else {
      units.push({ unit: 'months', value: wholeMonths });
    }
    cursor = addMonthsClamped(start, consumedMonths);
  }

  const remainderMs = Math.max(0, end.getTime() - cursor.getTime());
  const wholeDays = Math.floor(remainderMs / DAY_MS);
  if (chain.includes('weeks')) {
    units.push({ unit: 'weeks', value: Math.floor(wholeDays / 7) });
    if (chain.includes('days')) {
      units.push({ unit: 'days', value: wholeDays % 7 });
    }
  } else if (chain.includes('days')) {
    units.push({ unit: 'days', value: wholeDays });
  }

  const subDayMs = remainderMs % DAY_MS;
  const hours = Math.floor(subDayMs / (1000 * 60 * 60));
  const minutes = Math.floor((subDayMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((subDayMs % (1000 * 60)) / 1000);

  return { units, hours, minutes, seconds, isPast };
};

/**
 * Mon–Fri calendar days remaining: every weekday strictly after `from`'s
 * calendar date, up to and including `to`'s.
 */
export const countWorkdays = (from: Date, to: Date): number => {
  const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1);
  const last = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  let count = 0;
  while (cursor <= last) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) {
      count += 1;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

export const formatTimerDuration = (timer: Timer, now = new Date()): string => {
  const remaining = calculateTimeRemaining(timer, now);
  if (remaining.days > 0) {
    return `${remaining.days}d ${remaining.hours}h ${remaining.minutes}m`;
  }
  return `${remaining.hours}h ${remaining.minutes}m ${remaining.seconds}s`;
};

export const formatRecurrence = (timer: Timer): string => {
  if (!timer.is_recurring || timer.recurrence_type === 'none') {
    return 'None';
  }
  const interval = Math.max(1, timer.recurrence_interval || 1);
  if (timer.recurrence_type === 'custom') {
    const unit = timer.recurrence_unit || 'days';
    return interval === 1 ? `Every ${unit.slice(0, -1)}` : `Every ${interval} ${unit}`;
  }
  const unitByType: Record<Exclude<TimerRecurrence, 'none' | 'custom'>, TimerRecurrenceUnit> = {
    daily: 'days',
    weekly: 'weeks',
    monthly: 'months',
    yearly: 'years',
  };
  const unit = unitByType[timer.recurrence_type as Exclude<TimerRecurrence, 'none' | 'custom'>];
  return interval === 1
    ? `Every ${unit.slice(0, -1)}`
    : `Every ${interval} ${unit}`;
};

const TimerCard: React.FC<TimerCardProps> = ({
  timer,
  onEdit,
  onDelete,
  onStart,
  onPause,
  onReset,
  onArchive,
  onRestore,
  compact = false,
  now,
}) => {
  const [internalNow, setInternalNow] = useState<Date>(() => new Date());
  const durationDisplay = useLifeEventsDisplaySettings();
  const { cardContentPaddingSx } = useLayoutTokens();

  useEffect(() => {
    if (now !== undefined) {
      return undefined;
    }
    const interval = setInterval(() => {
      setInternalNow(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [now]);

  const effectiveNow = now !== undefined ? new Date(now) : internalNow;
  const timeRemaining = calculateTimeRemaining(timer, effectiveNow);

  const isCountUp = timer.timer_type === 'count_up';
  const isArchived = Boolean(timer.archived_at);
  const prefix = isCountUp ? 'Since' : (timeRemaining.isPast ? 'Passed' : 'Until');
  const displayTarget = getTimerDisplayTarget(timer, effectiveNow);
  const visibleTags = timer.tags.slice(0, compact ? 2 : 4);

  if (compact) {
    return (
      <Card
        sx={{
          mb: 1,
          borderLeft: `4px solid ${timer.color}`,
          bgcolor: timer.is_active ? 'background.paper' : 'action.disabledBackground',
        }}
      >
        {/* The `compact` PROP variant (Dashboard's timer list), not the layout
            density preference. Its tightening is the variant's whole purpose,
            so it keeps its own literal — swapping in cardContentPaddingSx made
            this branch byte-identical to the full card below and erased the
            distinction the prop exists to express. */}
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }} noWrap>
                {timer.title}
              </Typography>
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                {formatTimerDuration(timer, effectiveNow)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
              {visibleTags.map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
              <Chip
                label={isCountUp ? 'Elapsed' : 'Remaining'}
                size="small"
                color={isCountUp ? 'info' : 'primary'}
                variant="outlined"
              />
            </Stack>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const rollup = computeDurationRollup(timer, durationDisplay, effectiveNow);
  const digitSegments = [
    ...rollup.units.map((entry) => ({
      key: entry.unit as string,
      text: String(entry.value),
      label: entry.unit as string,
    })),
    ...(durationDisplay.duration_show_clock
      ? [
          { key: 'hours', text: String(rollup.hours).padStart(2, '0'), label: 'hours' },
          { key: 'minutes', text: String(rollup.minutes).padStart(2, '0'), label: 'mins' },
          { key: 'seconds', text: String(rollup.seconds).padStart(2, '0'), label: 'secs' },
        ]
      : []),
  ];
  let workdaysRemaining: number | null = null;
  if (durationDisplay.duration_show_workdays && !isCountUp) {
    workdaysRemaining = timeRemaining.isPast ? 0 : countWorkdays(effectiveNow, displayTarget);
  }

  return (
    <Card
      sx={{
        height: '100%',
        borderTop: `4px solid ${timer.color}`,
        bgcolor: timer.is_active ? 'background.paper' : 'action.disabledBackground',
        opacity: timer.is_active ? 1 : 0.7,
      }}
    >
      <CardContent sx={cardContentPaddingSx}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={2}>
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
            {isCountUp ? <TimerIcon color="info" /> : <HourglassEmptyIcon color="primary" />}
            <Typography variant="h6" component="h3" sx={{ fontWeight: 500 }} noWrap>
              {timer.title}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            {/* Archived cards drop the run controls: the only ways forward
                are Restore (reversible, no confirmation), Edit, or Delete. */}
            {!isArchived && onStart && !timer.is_active && (
              <Tooltip title="Start">
                <IconButton size="small" onClick={() => onStart(timer)} aria-label="start timer">
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isArchived && onPause && timer.is_active && (
              <Tooltip title="Pause">
                <IconButton size="small" onClick={() => onPause(timer)} aria-label="pause timer">
                  <PauseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isArchived && onReset && (
              <Tooltip title="Reset">
                <IconButton size="small" onClick={() => onReset(timer)} aria-label="reset timer">
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isArchived && onRestore && (
              <Tooltip title="Restore">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onRestore(timer)}
                  aria-label="restore timer"
                >
                  <UnarchiveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isArchived && onArchive && (
              <Tooltip title="Archive">
                <IconButton
                  size="small"
                  onClick={() => onArchive(timer)}
                  aria-label="archive timer"
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(timer)} aria-label="edit timer">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDelete(timer)} color="error" aria-label="delete timer">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {timer.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {timer.description}
          </Typography>
        )}

        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {prefix}
          </Typography>

          <Box
            display="flex"
            justifyContent="center"
            gap={1}
            flexWrap="wrap"
            sx={{ mb: 1 }}
          >
            {digitSegments.map((segment, index) => (
              <React.Fragment key={segment.key}>
                {index > 0 && (
                  <Typography variant="h4" color="text.disabled" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>:</Typography>
                )}
                <Box sx={{ textAlign: 'center', minWidth: index === 0 ? 60 : 50 }}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                    {segment.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {segment.label}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>

          {workdaysRemaining !== null && (
            <Typography variant="caption" color="text.secondary" display="block">
              {`${workdaysRemaining} ${workdaysRemaining === 1 ? 'workday' : 'workdays'} remaining`}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Chip
            label={isCountUp ? 'Count Up' : 'Countdown'}
            size="small"
            color={isCountUp ? 'info' : 'primary'}
            variant="outlined"
          />
          {isArchived ? (
            <Chip icon={<ArchiveIcon />} label="Archived" size="small" variant="outlined" />
          ) : (
            <Chip
              label={timer.is_active ? 'Active' : 'Paused'}
              size="small"
              color={timer.is_active ? 'success' : 'default'}
              variant="outlined"
            />
          )}
          <Chip
            label={formatRecurrence(timer)}
            size="small"
            variant="outlined"
            color={timer.is_recurring ? 'secondary' : 'default'}
          />
          {visibleTags.map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
          {timer.tags.length > visibleTags.length && (
            <Chip label={`+${timer.tags.length - visibleTags.length}`} size="small" variant="outlined" />
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary" display="block" mt={2}>
          Target: {displayTarget.toLocaleString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </Typography>
        {timer.last_reset_at && (
          <Typography variant="caption" color="text.secondary" display="block">
            Reset: {new Date(timer.last_reset_at).toLocaleString()}
          </Typography>
        )}
        {timer.archived_at && (
          <Typography variant="caption" color="text.secondary" display="block">
            Archived: {new Date(timer.archived_at).toLocaleString()}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default TimerCard;
